import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, Line, LineChart, CartesianGrid, ReferenceLine } from "recharts";
import { fmtUsd } from "@/lib/api";
import { Hint } from "@/components/Hint";
import { LiveChart } from "@/components/LiveChart";
import { Candles } from "@/components/Candles";
import { OrderBook } from "@/components/OrderBook";
import { AdvancedSection } from "@/components/Beginner";

export const RANGES = [
  { label: "LIVE", days: "live" }, { label: "24H", days: "1" }, { label: "7D", days: "7" },
  { label: "30D", days: "30" }, { label: "90D", days: "90" }, { label: "1Y", days: "365" },
];
const TT = { background: "#0F141C", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 };

// A chart point is "delayed" once it is older than roughly two bars of the range's upstream granularity (30-min / 4-hour / 4-day candles).
const STALE_AFTER_MIN = { live: 180, 1: 180, 7: 480, 30: 480, 90: 5 * 1440, 365: 5 * 1440 };
const chartFreshness = (lastMs, range) => {
  const ageMin = Math.max(0, Math.round((Date.now() - lastMs) / 60000));
  const delayed = ageMin > (STALE_AFTER_MIN[range] ?? 480);
  const when = new Date(lastMs).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  return { delayed, text: `${delayed ? "delayed · " : ""}chart data as of ${when}` };
};

const ON = {
  cyan: "border-cyan-800 text-cyan-300 bg-cyan-950/40", violet: "border-violet-800 text-violet-300 bg-violet-950/40",
  amber: "border-amber-800 text-amber-300 bg-amber-950/40", emerald: "border-emerald-800 text-emerald-300 bg-emerald-950/40",
};
const Toggle = ({ k, tid, on, onClick, color, children }) => (
  <Hint k={k} asChild>
    <button data-testid={tid} onClick={onClick} className={`px-2 py-1 rounded border ${on ? ON[color] : "border-slate-800 text-slate-500"}`}>{children}</button>
  </Hint>
);

export const CoinChartPanel = ({ coin, range, setRange, isLive, live, book, chartMode, setChartMode, liveInterval, setLiveInterval,
  ohlc, combined, up, flags, setFlags, alertCount, canLive = true, histAsOf }) => {
  const { showMA, showBB, showRsi, showMacd } = flags;
  const flip = (k) => setFlags((f) => ({ ...f, [k]: !f[k] }));
  const freshness = !isLive && histAsOf ? chartFreshness(histAsOf, range) : null;
  return (
    <div className="apx-card p-4" data-testid="coin-chart-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex gap-1 bg-slate-900 rounded-lg p-1 border border-slate-800">
          {RANGES.filter((r) => canLive || r.days !== "live").map((r) => (
            <Hint key={r.days} k={`range_${r.days}`} asChild>
              <button data-testid={`range-${r.label}`} onClick={() => setRange(r.days)}
                className={`px-3 py-1 rounded-md text-xs font-mono-ticker flex items-center gap-1.5 ${range === r.days ? (r.days === "live" ? "bg-emerald-500/15 text-emerald-300" : "bg-cyan-500/15 text-cyan-300") : "text-slate-400 hover:text-slate-200"}`}>
                {r.days === "live" && <span className={`inline-flex h-1.5 w-1.5 rounded-full ${range === "live" ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`} />}
                {r.label}
              </button>
            </Hint>
          ))}
        </div>
        <div className="flex gap-2 text-[11px] font-mono-ticker">
          {!isLive && <div className="flex gap-1 bg-slate-900 rounded-lg p-1 border border-slate-800 mr-1">
            {["candles", "line"].map((m) => (
              <Hint key={m} title={m === "candles" ? "Candlesticks" : "Line"} text={m === "candles" ? "OHLC candles from CoinGecko: 30-min candles for 24H, 4-hour for 7D-30D, 4-day for 90D+." : "Closing-price line with optional moving averages and Bollinger Bands."} asChild>
                <button data-testid={`chart-mode-${m}`} onClick={() => setChartMode(m)} className={`px-2 py-0.5 rounded-md capitalize ${chartMode === m ? "bg-cyan-500/15 text-cyan-300" : "text-slate-400 hover:text-slate-200"}`}>{m}</button>
              </Hint>
            ))}
          </div>}
          <Toggle k="toggle_ma" tid="toggle-ma" on={showMA} onClick={() => flip("showMA")} color="cyan">MA</Toggle>
          <Toggle k="toggle_bb" tid="toggle-bb" on={showBB} onClick={() => flip("showBB")} color="violet">BB</Toggle>
          {!isLive && <Toggle k="toggle_rsi" tid="toggle-rsi" on={showRsi} onClick={() => flip("showRsi")} color="amber">RSI</Toggle>}
          {!isLive && <Toggle k="toggle_macd" tid="toggle-macd" on={showMacd} onClick={() => flip("showMacd")} color="emerald">MACD</Toggle>}
        </div>
      </div>

      {isLive ? (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-4">
          <div className="min-w-0">
            <LiveChart live={live} symbol={coin.symbol} mode={chartMode} onMode={setChartMode} interval={liveInterval} onInterval={setLiveInterval} showMA={showMA} showBB={showBB} />
            {alertCount > 0 && (
              <div className="mt-2 text-[10px] font-mono-ticker text-cyan-300/80 inline-flex items-center gap-1.5" data-testid="live-alert-watch">
                <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75 animate-ping" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-400" /></span>
                Watching {alertCount} price alert{alertCount > 1 ? "s" : ""} on the live feed — fires instantly when crossed
              </div>
            )}
          </div>
          {book !== undefined && (
            <AdvancedSection title="Order book" k="order_book" testid="advanced-orderbook"
              what="Live buy orders (green, below price) and sell orders (red, above price) waiting on the exchange."
              why="Big walls of orders often act as short-term support or resistance.">
              <OrderBook book={book} symbol={coin.symbol} />
            </AdvancedSection>
          )}
        </div>
      ) : chartMode === "candles" ? (
        ohlc.length ? (
          <Candles data={ohlc} showMA={showMA} showBB={showBB} timeFormatter={(t) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: range === "1" || range === "7" ? "2-digit" : undefined })} testid="price-candles" />
        ) : <div style={{ height: 320 }} className="skeleton" data-testid="price-candles-loading" />
      ) : (
        <div style={{ width: "100%", height: 320 }} data-testid="price-chart">
          <ResponsiveContainer>
            <AreaChart data={combined}>
              <defs><linearGradient id="priceG" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={up ? "#10B981" : "#EF4444"} stopOpacity={0.5} /><stop offset="1" stopColor={up ? "#10B981" : "#EF4444"} stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="time" stroke="#64748b" fontSize={10} tickLine={false} minTickGap={30} />
              <YAxis stroke="#64748b" fontSize={10} domain={["auto", "auto"]} tickFormatter={(v) => fmtUsd(v, { digits: 2 })} width={70} />
              <Tooltip contentStyle={TT} formatter={(v, k) => [fmtUsd(v), k === "p" ? "Price" : k]} />
              <Area type="monotone" dataKey="p" stroke={up ? "#10B981" : "#EF4444"} strokeWidth={2} fill="url(#priceG)" />
              {showMA && <Line type="monotone" dataKey="ma20" stroke="#06B6D4" strokeWidth={1.3} dot={false} />}
              {showMA && <Line type="monotone" dataKey="ma50" stroke="#8B5CF6" strokeWidth={1.3} dot={false} />}
              {showBB && <Line type="monotone" dataKey="bbUp" stroke="#8B5CF6" strokeWidth={1} strokeDasharray="3 3" dot={false} />}
              {showBB && <Line type="monotone" dataKey="bbLo" stroke="#8B5CF6" strokeWidth={1} strokeDasharray="3 3" dot={false} />}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {freshness && (
        <div className={`mt-2 text-[10px] font-mono-ticker ${freshness.delayed ? "text-amber-300" : "text-slate-500"}`} data-testid="chart-freshness-label">{freshness.text}</div>
      )}

      {!isLive && showRsi && (
        <SubChart tid="rsi-chart" title="RSI (14)" color="text-amber-300" data={combined} domain={[0, 100]} width={30}>
          <ReferenceLine y={70} stroke="#EF4444" strokeDasharray="3 3" /><ReferenceLine y={30} stroke="#10B981" strokeDasharray="3 3" />
          <Line type="monotone" dataKey="rsi" stroke="#F59E0B" strokeWidth={1.4} dot={false} />
        </SubChart>
      )}
      {!isLive && showMacd && (
        <SubChart tid="macd-chart" title="MACD (12, 26, 9)" color="text-emerald-300" data={combined} width={40}>
          <ReferenceLine y={0} stroke="#64748b" />
          <Line type="monotone" dataKey="macd" stroke="#06B6D4" strokeWidth={1.4} dot={false} />
          <Line type="monotone" dataKey="macdSignal" stroke="#F59E0B" strokeWidth={1.2} dot={false} />
        </SubChart>
      )}
    </div>
  );
};

const SubChart = ({ tid, title, color, data, domain, width, children }) => (
  <div className="mt-2 pt-2 border-t border-slate-900" data-testid={tid}>
    <div className={`text-[10px] font-mono-ticker uppercase tracking-widest mb-1 ${color}`}>{title}</div>
    <div style={{ width: "100%", height: 90 }}>
      <ResponsiveContainer>
        <LineChart data={data}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="time" hide />
          <YAxis domain={domain} stroke="#64748b" fontSize={10} width={width} />
          <Tooltip contentStyle={TT} />
          {children}
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);
